import * as Geo from "@/base/geo"
import type { MeasurementPreference } from "@/base/sensor-conversions"
import type { Reducer } from "@reduxjs/toolkit"

import * as AppStorage from "@/base/async-storage"
import { createSelector, createSlice } from "@reduxjs/toolkit"
import type { RootState } from "./root.reducer"
import { getMapTypeIdFromState } from "./selectors"
import { useShallowEqualSelector } from "./useRootSelector"
import { Theme } from "@/theme"
import { ENVIRONMENT } from "@/base/environment"

interface UserPreferencesState {
  fieldColorPreference: AppStorage.FieldColorScheme
  mapTypeId: Geo.MapTypeId
  measurement: MeasurementPreference
}

const initialState: UserPreferencesState = {
  fieldColorPreference: AppStorage.STORAGE_DEFAULTS.fieldColorPreference,
  mapTypeId: AppStorage.STORAGE_DEFAULTS.mapTypeId,
  measurement:
    ENVIRONMENT.locale.measurementSystem === "metric" ? "metric" : "us",
}
const slice = createSlice({
  extraReducers: (builder) => {
    return builder
      .addCase(
        AppStorage.setMeasurementPreference.pending,
        (state, action) => {
          state.measurement = action.meta.arg
        },
      )
      .addCase(AppStorage.setFieldColorPreference.pending, (state, action) => {
        state.fieldColorPreference = action.meta.arg
      })
      .addCase(AppStorage.setMapTypeId.pending, (state, action) => {
        state.mapTypeId = action.meta.arg
      })
      .addCase(
        AppStorage.readLocalStorageAsync.fulfilled,
        (state, { payload: storedValues }) => {
          state.fieldColorPreference = storedValues.fieldColorPreference
          state.mapTypeId = storedValues.mapTypeId
          state.measurement = storedValues.measurementSystem
        },
      )
  },
  initialState,
  name: "user",
  reducers: {},
})

export function getMapTypeValueFromState(
  state: RootState,
): "hybrid" | "satellite" | "standard" {
  const id = getMapTypeIdFromState(state)
  return Geo.MapTypeIds[id].default
}
export function getFieldColorPreferenceFromState(
  state: RootState,
): AppStorage.FieldColorScheme {
  return state.userPreferences.fieldColorPreference
}

export const getFieldColorsFromState = createSelector(
  getMapTypeIdFromState,
  (state: RootState, override?: AppStorage.FieldColorScheme) => {
    return override ?? getFieldColorPreferenceFromState(state)
  },
  (mapTypeId, colorscheme) =>
    getFieldColorsForSchemePreference({
      colorScheme: colorscheme,
      mapTypeId,
    }),
)

export const DEFAULT_FIELD_COLOR: AppStorage.FieldColorScheme = "yellow"

/**
 * Get the field colors for a given color scheme
 */
function getFieldColorsForSchemePreference({
  colorScheme,
  mapTypeId,
}: {
  colorScheme?: AppStorage.FieldColorScheme
  mapTypeId: Geo.MapTypeId
}) {
  if (typeof colorScheme === "undefined") {
    colorScheme = AppStorage.STORAGE_DEFAULTS.fieldColorPreference
  }

  const swatch = Theme.COLORS[`$${colorScheme}`]
  const fillColor: string = swatch[400]
  let strokeColor: string = swatch[700]
  let svgColor: string = swatch[500]
  switch (colorScheme) {
    case "amber": {
      strokeColor = swatch[100]
      break
    }
    case "yellow": {
      svgColor = swatch[400]
      strokeColor = swatch[400]
      break
    }
    case "green": {
      if (mapTypeId === "TERRAIN") {
        break
      } else {
        strokeColor = swatch[200]
        break
      }
    }
    case "red": {
      strokeColor = swatch[800]
      svgColor = swatch[700]
      break
    }
    case "orange": {
      strokeColor = swatch[300]
      svgColor = swatch[400]
      break
    }
    case "dark": {
      if (mapTypeId === "TERRAIN") {
        break
      } else {
        strokeColor = swatch[700]
        break
      }
    }
    case "darkBlue":
    case "indigo": {
      strokeColor = swatch[100]
      break
    }
  }
  return { fillColor, scheme: colorScheme, strokeColor, svgColor }
}

// export interface UseFieldColorsReturn {
//   fillColor: string
//   scheme: AppStorage.FieldColorScheme
//   strokeColor: string
//   svgColor: string
// }

export function useFieldColors(override?: AppStorage.FieldColorScheme) {
  return useShallowEqualSelector((state) =>
    getFieldColorsFromState(state, override),
  )
}

const userPreferences: Reducer<UserPreferencesState> = slice.reducer
export default userPreferences
